import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  role: string;
  avatar_url?: string | null;
  created_at?: string;
  updated_at?: string;
  parent_type?: string | null;
  work_status?: string | null;
  breastfeeding?: boolean | null;
  single_parent?: boolean | null;
  nap_times?: any;
  district?: string | null;
  experience_level?: string | null;
  qualifications?: string[] | null;
  availability_type?: string | null;
  is_business_owner?: boolean | null;
}

export const useUserProfile = () => {
  const { user } = useAuth();

  const cacheKey = user ? `bb-profile-cache-${user.id}` : '';
  const readCache = () => {
    try {
      const raw = cacheKey ? localStorage.getItem(cacheKey) : null;
      return raw ? (JSON.parse(raw) as UserProfile) : undefined;
    } catch { return undefined; }
  };

  const { data: profile = null, isLoading } = useQuery({
    queryKey: ['user-profile', user?.id],
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    initialData: readCache,
    initialDataUpdatedAt: 0,
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, role, avatar_url, created_at, updated_at, bio, languages, parent_type, work_status, breastfeeding, single_parent, nap_times, district, experience_level, qualifications, availability_type, profile_hidden, is_business_owner, children_info, open_to_meet, open_to_meet_since, company_name, newsletter_subscribed, onboarding_installed, onboarding_browsed')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      const result = { ...data, email: user.email ?? '' } as UserProfile;
      try {
        localStorage.setItem(`bb-profile-cache-${user.id}`, JSON.stringify({ id: result.id, first_name: result.first_name, role: result.role, avatar_url: result.avatar_url }));
      } catch { /* ignore */ }
      return result;
    },
  });

  return { profile: user ? profile : null, loading: !!user && isLoading };
};
